import { Component, Input } from '@angular/core';
import { PlatformEnum } from '@panamax/app-state';
import {
  FilePicker,
  PickFilesOptions,
} from '@capawesome/capacitor-file-picker';
import { b64toBlob } from '@shared/helpers/file.helpers';
import {
  CSV_LIMITED_EXTENSIONS,
  XLSX_LIMITED_EXTENSIONS,
} from '@shared/services/file/model/file.model';
import {
  REQUIRED_COLUMNS,
  OPTIONAL_COLUMNS,
  INSTRUCTION_STRINGS,
  VALID_CASE_STRINGS,
  VALID_EACH_STRINGS,
  VALIDATION_COLUMNS,
} from '../../../models/import-non-usf-models';
import { DocumentImportService } from '@shared/services/import-documents/document-import.service';
import { read, utils } from 'xlsx';
import emojiRegex from 'emoji-regex';
import { NonUsfProductImportRequest } from '@usf/product-types';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-import-non-usf-product-modal',
  templateUrl: './import-non-usf-product-modal.component.html',
  styleUrls: ['./import-non-usf-product-modal.component.scss'],
})
export class ImportNonUsfProductModalComponent {
  @Input() platform: PlatformEnum;
  @Input() dismissEvent: Function;
  @Input() downloadTemplateEvent: Function;
  @Input() downloadGlCodeEvent: Function;
  @Input() submitEvent: Function;
  @Input() showDownloadGlCode: boolean;
  platformRef = PlatformEnum;
  file: File;
  fileData = [];
  isFileValid = false;
  displayPageError = new BehaviorSubject(false);
  pageError = '';
  startingRowNumber = 4;

  constructor(readonly documentImportService: DocumentImportService) {}

  dismissModal = () => {
    this.dismissEvent(false);
  };

  submit() {
    this.submitEvent(this.fileData);
    this.dismissEvent(false);
  }

  handleFileAddRequest = async () => {
    try {
      const selectedFiles = await FilePicker.pickFiles({
        types: [...CSV_LIMITED_EXTENSIONS, ...XLSX_LIMITED_EXTENSIONS],
        limit: 1,
        multiple: false,
        readData: true,
      } as PickFilesOptions);
      let file: File;

      if (selectedFiles.files.length > 0) {
        selectedFiles.files[0].blob = b64toBlob(selectedFiles.files[0].data);
        file = new File(
          [selectedFiles.files[0].blob],
          selectedFiles.files[0].name,
          { type: selectedFiles.files[0].mimeType },
        );
        this.processFile(file);
      }
    } catch (ignore) {}
  };

  handleFileDeleteRequest() {
    this.file = undefined;
    this.isFileValid = false;
    this.displayPageError.next(false);
  }

  handleDownloadNonUsfTemplate() {
    this.downloadTemplateEvent();
  }

  handleDownloadGlCode() {
    this.downloadGlCodeEvent();
  }

  processFile = (file: File) => {
    this.displayPageError.next(false);
    if (this.validateFileExtension(file.type)) {
      this.validateUploadedNonUsfProductsTemplate(file);
      this.isFileValid = true;
    } else {
      this.isFileValid = false;
      this.displayPageError.next(true);
    }

    this.file = this.isFileValid ? file : undefined;
  };

  validateFileExtension(fileExtension: string): boolean {
    const accpetedFormats = [
      ...CSV_LIMITED_EXTENSIONS,
      ...XLSX_LIMITED_EXTENSIONS,
    ];
    return accpetedFormats.includes(fileExtension);
  }

  validateUploadedNonUsfProductsTemplate = (file: File) => {
    const blob = file;
    const fileReader = new FileReader();
    const isExcelFile = file.name.endsWith('.xlsx');

    fileReader.onload = async ev => {
      if (isExcelFile) {
        // Convert Excel to CSV
        const arrayBuffer = ev.target.result;
        const workbook = read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        let csvData = utils.sheet_to_csv(worksheet);
        // Process the CSV data
        await this.validateFileContents({ currentTarget: { result: csvData } });
      } else {
        // Process the CSV data directly
        await this.validateFileContents(ev);
      }
    };

    if (isExcelFile) {
      fileReader.readAsArrayBuffer(blob);
    } else {
      fileReader.readAsText(blob);
    }
  };

  validateFileContents = async (fileConents: any) => {
    let dataString = fileConents.currentTarget.result;

    // Remove emojis characters
    const emojiPattern = emojiRegex();
    dataString = dataString.replace(emojiPattern, '??');

    // Remove rows that contain only commas
    const rows = dataString.split('\n');
    const filteredRows = rows.filter(row => {
      // Check if the row contains anything other than commas, whitespace, or return characters
      if (!row || row === '\r' || row === '\n' || row === '') {
        return false;
      }
      // Check if the row contains anything other than commas or whitespace
      let temp =
        row.trim() &&
        !row
          .split('')
          .every(
            char =>
              char === ',' ||
              char === ' ' ||
              char === '\t' ||
              char === '\n' ||
              char === '\r',
          );
      return temp;
    });
    dataString = filteredRows.join('\n');
    // Ensure there are no escape characters in the last 2 characters of dataString
    if (dataString.length >= 2) {
      const lastTwoChars = dataString.slice(-2);
      if (lastTwoChars.includes('\r') || lastTwoChars.includes('\n')) {
        // Remove trailing newlines or carriage returns
        dataString = dataString.replace(/[\r\n]+$/, '');
      }
    }

    const checkArray = dataString.split(',');
    let isFileValid = false;
    if (checkArray.length > 11) {
      for (let i = 0; i < 11; i++) {
        if (VALIDATION_COLUMNS.has(checkArray[i].toLowerCase())) {
          isFileValid = true;
          this.setValidityAndErrorMessageStatus(isFileValid);
        } else {
          isFileValid = false;
          this.setValidityAndErrorMessageStatus(isFileValid);
          break;
        }
      }
    }
    if (isFileValid) {
      let csvArray = (await this.documentImportService.breakDownCSV(
        dataString,
      )) as string[][];
      //Instruction rows
      csvArray = csvArray.filter(
        (row, i) => !this.isFieldHeaderOrInstruction(row, i) && row.length > 1,
      );
      let objectsforImport =
        this.documentImportService.trimCSVArraytoJustDesiredColumns(
          [...REQUIRED_COLUMNS],
          csvArray,
          [...OPTIONAL_COLUMNS],
        );

      if (objectsforImport.length === 0) {
        isFileValid = false;
        this.setValidityAndErrorMessageStatus(isFileValid);
      }

      //checks if the required fields are present and product number is a number type
      isFileValid = this.validateRequiredFields(csvArray);
      this.setValidityAndErrorMessageStatus(isFileValid);

      if (isFileValid) {
        // converts the papa parse data to a usable object for our ngrx layer
        const csvRows: NonUsfProductImportRequest[] =
          this.convertStringsToImportRows(objectsforImport);
        if (csvRows.length === 0) {
          isFileValid = false;
          this.setValidityAndErrorMessageStatus(isFileValid);
        } else {
          this.fileData = csvRows;
        }
      }
    }
  };

  validateRequiredFields(fileFields: string[][]) {
    //index is 1 because column 0 is the header
    for (let i = 1; i < fileFields.length; i++) {
      if (!fileFields[i][0] && !fileFields[i][1]) {
        return false;
      }
      if (fileFields[i][1] && !this.isNumber(fileFields[i][1])) {
        return false;
      }
    }
    return true;
  }

  convertStringsToImportRows(objectsforList) {
    return objectsforList.map((object, i) => {
      let importRow = {} as NonUsfProductImportRequest;
      importRow.rowNumber = i + this.startingRowNumber;
      if (object['product name / description'])
        importRow.productDescLong = object['product name / description'];
      if (object['product number'])
        importRow.productNumber = Number(object['product number']);
      if (object['vendor code']) importRow.vendorId = object['vendor code'];
      if (object['vendor']) importRow.vendorId = object['vendor'];
      if (object['brand']) importRow.brand = object['brand'];
      if (object['gl code'].length) importRow.glCode = object['gl code'];
      if (object['manufacturer #'])
        importRow.manufacturerProductNumber = object['manufacturer #'];
      if (object['pack size']) importRow.salesPackSize = object['pack size'];
      if (object['price ($)']) importRow.priceCSV = object['price ($)'];
      if (object['avg net weight (lbs)'])
        importRow.averageNetWeightCSV = object['avg net weight (lbs)'];
      if (object['units per case'])
        importRow.unitsPerCaseCSV = object['units per case'];
      if (!object['units per case'] || object['units per case'] === '0') {
        importRow.unitsPerCaseCSV = '1';
      }
      //price uom on the template actually tartgets isPricePerUnitInd
      if (object['price uom']) {
        if (
          VALID_CASE_STRINGS.some(
            str => str === object['price uom'].toLowerCase(),
          )
        ) {
          importRow.isPricePerUnitInd = false;
        }
        if (
          VALID_EACH_STRINGS.some(
            str => str === object['price uom'].toLowerCase(),
          )
        ) {
          importRow.isPricePerUnitInd = true;
        }
      }
      // unit description on the template actually targets price uom
      if (object['unit description'])
        importRow.priceUom = object['unit description'];
      return { ...importRow };
    });
  }

  isNumber(value: string) {
    const numberForCheck = Number(value);
    return !Number.isNaN(numberForCheck) && numberForCheck !== 0;
  }

  setValidityAndErrorMessageStatus(isValid: boolean) {
    this.isFileValid = isValid;
    this.displayPageError.next(!isValid);
  }

  isFieldHeaderOrInstruction(row: string[], index: number): boolean {
    for (const data of row) {
      if (INSTRUCTION_STRINGS.has(data.toLowerCase())) {
        return true;
      }
    }
    if (index === 1 || index === 2) {
      this.startingRowNumber--;
    }
    return false;
  }
}
